import type { PluginListenerHandle } from '@capacitor/core';

import type { WsMessage } from '#features/realtime/wsMessageParser';
import { envConfig } from '#constants/envConfig';
import { watchWebForegroundNotifications } from './webForegroundNotifications';

export type ForegroundPush = Record<string, unknown>;

export interface ForegroundNotification {
  message: WsMessage;
  text: string;
  href?: string;
  recipientId?: string;
}

const stringField = (data: ForegroundPush, key: string): string | undefined =>
  typeof data[key] === 'string' && data[key] ? data[key] : undefined;

const controlTypes = new Set(['CALL_INCOMING', 'CALL_DISMISS', 'THREAD_DISMISS']);

const internalHref = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  try {
    const url = new URL(value);
    const apiOrigin = new URL(envConfig.API_URL).origin;
    if (url.origin !== apiOrigin || !['https:', 'http:'].includes(url.protocol)) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

/** Native alert text is already localized by the server; WS keeps its Lingui text. */
export function parseForegroundNotification(data: ForegroundPush): ForegroundNotification | null {
  const notificationId = stringField(data, 'notificationId');
  if (!notificationId) return null;
  const title = stringField(data, 'title');
  const body = stringField(data, 'body');
  const text =
    title && body && title !== body && title !== 'Teamplay' ? `${title}\n${body}` : (body ?? title);
  if (!text) return null;
  const type = stringField(data, 'eventType') ?? 'NATIVE_PUSH';
  if (controlTypes.has(type) || controlTypes.has(stringField(data, 'type') ?? '')) return null;
  const actorId = stringField(data, 'actorId');
  const parentId =
    stringField(data, 'parentId') ??
    (stringField(data, 'tag')?.startsWith('chat:') ? String(data.tag).slice(5) : undefined);
  const targetId = stringField(data, 'targetId');
  return {
    text,
    href: internalHref(stringField(data, 'click_action') ?? stringField(data, 'target')),
    recipientId: stringField(data, 'recipientId'),
    message: {
      type,
      actor: actorId
        ? {
            userId: actorId,
            displayName: stringField(data, 'actorName') ?? '',
            avatar: stringField(data, 'image') ?? null,
          }
        : null,
      data: {
        notificationId,
        messageKey: null,
        params: null,
        parentId: parentId ?? null,
        targetId: targetId ?? null,
        gameId: stringField(data, 'gameId') ?? null,
        hasNotification: true,
      },
    },
  };
}

/** Own the async listener even when auth or the hook disappears during registration. */
export function watchNativeForegroundNotifications(
  onNotification: (data: ForegroundPush) => void
): () => void {
  if (!envConfig.IS_NATIVE) return watchWebForegroundNotifications(onNotification);
  let disposed = false;
  let handle: PluginListenerHandle | undefined;
  if (envConfig.IS_NATIVE) {
    void (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        const plugin = registerPlugin<{
          start(): Promise<void>;
          addListener(
            event: 'foregroundNotification',
            callback: (data: ForegroundPush) => void
          ): Promise<PluginListenerHandle>;
        }>('NotificationPresentation');
        handle = await plugin.addListener('foregroundNotification', (data) => {
          if (!disposed) onNotification(data);
        });
        if (disposed) await handle.remove();
        else await plugin.start().catch(() => undefined);
      } catch {
        // Older native shells retain their existing system delivery fallback.
      }
    })();
  }
  return () => {
    disposed = true;
    void handle?.remove();
  };
}
